import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ArrowRight, Eye, EyeOff, ShoppingBag } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [registroPendiente, setRegistroPendiente] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'vendedor'  // Rol por defecto
  });

  // Cargar logo de marca
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await fetch(`${API_URL}/brand/logo`);
        const data = await response.json();
        if (data?.logo_url) {
          setLogoUrl(`${API_URL}/brand/logo/image?v=${encodeURIComponent(data.updated_at || Date.now())}`);
        }
      } catch (error) {
        console.debug('Brand logo not available:', error.message);
      }
    };
    fetchLogo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        const response = await register(formData.username, formData.email, formData.password, formData.role);
        
        // Verificar si la cuenta quedó pendiente de aprobación
        if (response.status === 'pendiente') {
          setRegistroPendiente(true);
          toast.success('Cuenta creada. Pendiente de aprobación.');
        } else {
          toast.success('Cuenta creada exitosamente');
          // Redirigir según el rol
          if (formData.role === 'aliado') {
            navigate('/panel-aliado', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        }
      } else {
        const response = await login(formData.email, formData.password);
        toast.success('Bienvenido');
        
        // Redirigir según el rol del usuario
        const userRole = response.user?.role;
        if (userRole === 'aliado') {
          navigate('/panel-aliado', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al procesar la solicitud';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Desktop: Left Panel with Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-black">
        <img
          src="https://images.unsplash.com/photo-1585412459212-8def26f7e84c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600"
          alt="Sastrería de lujo"
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-8">
            <div className="mb-8">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="Logo Juan Pablo Murillo" 
                  className="h-28 w-auto mx-auto object-contain brightness-0 invert"
                  data-testid="login-brand-logo"
                />
              ) : (
                <div className="h-28 w-28 mx-auto bg-white/10 animate-pulse rounded" />
              )}
            </div>
            <h1 className="font-serif text-4xl text-white tracking-wide mb-3">
              ATELIER <span className="text-white/50">—</span> JPM
            </h1>
            <p className="text-xs uppercase tracking-[0.25em] text-white/60">
              Juan Pablo Murillo Tailoring System
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile: Brand Header - Premium Design */}
        <div className="lg:hidden bg-black pt-safe">
          <div className="px-6 py-8 text-center">
            {/* Logo prominente */}
            <div className="mb-5">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="Logo Juan Pablo Murillo" 
                  className="h-20 w-auto mx-auto object-contain brightness-0 invert"
                  data-testid="login-brand-logo-mobile"
                />
              ) : (
                <div className="h-20 w-20 mx-auto bg-white/10 animate-pulse" />
              )}
            </div>
            {/* Nombre del sistema prominente */}
            <h1 className="font-serif text-2xl text-white tracking-wide mb-2">
              ATELIER <span className="text-white/40">—</span> JPM
            </h1>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">
              Juan Pablo Murillo Tailoring System
            </p>
            {/* Línea decorativa */}
            <div className="flex items-center justify-center gap-3 mt-5">
              <div className="w-8 h-px bg-white/20"></div>
              <div className="w-1.5 h-1.5 bg-[#D4AF37] rotate-45"></div>
              <div className="w-8 h-px bg-white/20"></div>
            </div>
          </div>
        </div>

        {/* Form Area */}
        <div className="flex-1 flex items-center justify-center px-6 py-8 sm:px-8 pb-safe">
          <div className="max-w-md w-full space-y-6">
            {/* Mensaje de cuenta pendiente */}
            {registroPendiente ? (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-serif text-2xl text-black mb-2">Cuenta Pendiente de Aprobación</h2>
                  <p className="text-zinc-600">
                    Su cuenta ha sido creada exitosamente y está pendiente de aprobación por un administrador.
                  </p>
                  <p className="text-sm text-zinc-500 mt-2">
                    Una vez aprobada, podrá iniciar sesión con sus credenciales.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setRegistroPendiente(false);
                    setIsRegister(false);
                    setFormData({ username: '', email: '', password: '', role: 'vendedor' });
                  }}
                  className="w-full h-12 bg-black hover:bg-zinc-800 text-white text-sm uppercase tracking-widest transition-all"
                >
                  Volver al Inicio de Sesión
                </button>
              </div>
            ) : (
              <>
                {/* Form Header */}
                <div className="text-center">
                  <h2 className="font-serif text-2xl md:text-3xl text-black mb-1">
                    {isRegister ? 'Crear Cuenta' : 'Bienvenido'}
                  </h2>
                  <p className="text-xs text-zinc-500 hidden lg:block">
                    ATELIER — JPM
                  </p>
                  <p className="text-sm text-zinc-500 lg:hidden">
                    {isRegister ? 'Complete sus datos para registrarse' : 'Ingrese sus credenciales'}
                  </p>
                </div>

            {!isRegister && (
              <button
                type="button"
                onClick={() => navigate('/tienda')}
                className="group flex w-full items-center gap-4 border border-black bg-white px-4 py-4 text-left transition-colors duration-300 hover:bg-black"
                data-testid="login-tienda-publica"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-black text-white transition-colors duration-300 group-hover:bg-white group-hover:text-black">
                  <ShoppingBag className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-black transition-colors duration-300 group-hover:text-white">Explora la colección y personaliza tu prenda</span>
                  <span className="block text-[10px] uppercase tracking-[0.15em] text-zinc-500 transition-colors duration-300 group-hover:text-zinc-300">Tienda online · compra sin cuenta</span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-black transition-[color,transform] duration-300 group-hover:translate-x-1 group-hover:text-white" />
              </button>
            )}

            <form onSubmit={handleSubmit} className="login-form space-y-5 text-black [color-scheme:light]">
              {isRegister && (
                <>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2 block font-medium">
                      Nombre de Usuario
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="w-full h-12 border-b-2 border-zinc-200 bg-transparent px-0 text-base focus:outline-none focus:border-black transition-colors"
                      data-testid="username-input"
                    />
                  </div>
                  
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2 block font-medium">
                      Tipo de Perfil
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full h-12 border-b-2 border-zinc-200 bg-transparent px-0 text-base focus:outline-none focus:border-black transition-colors"
                      data-testid="role-select"
                    >
                      <option value="vendedor">Vendedor</option>
                      <option value="diseñador">Diseñador</option>
                      <option value="asesor_diseñador">Asesor Diseñador</option>
                      <option value="aliado">Aliado (Wedding Planner, Hotel, etc.)</option>
                    </select>
                    <p className="text-xs text-zinc-400 mt-1">
                      Su cuenta quedará pendiente de aprobación por un administrador
                    </p>
                  </div>
                </>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2 block font-medium">
                  Correo Electrónico
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-12 border-b-2 border-zinc-200 bg-transparent px-0 text-base focus:outline-none focus:border-black transition-colors"
                  placeholder="correo@ejemplo.com"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2 block font-medium">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full h-12 border-b-2 border-zinc-200 bg-transparent px-0 pr-10 text-base focus:outline-none focus:border-black transition-colors"
                    placeholder="••••••••"
                    data-testid="password-input"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-zinc-400 hover:text-zinc-700 transition-colors focus:outline-none"
                    data-testid="toggle-password-visibility"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-col space-y-4 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-black text-white hover:bg-zinc-800 h-12 uppercase tracking-widest text-xs font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-button"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Procesando...</span>
                    </>
                  ) : (
                    <span>{isRegister ? 'Crear Cuenta' : 'Iniciar Sesión'}</span>
                  )}
                </button>

                {!isRegister && (
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-zinc-500 hover:text-black transition-colors py-2"
                    data-testid="forgot-password-link"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}

                <div className="relative py-3">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-zinc-200"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-3 bg-[#FAFAFA] text-xs text-zinc-400">o</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsRegister(!isRegister)}
                  className="text-xs uppercase tracking-widest text-zinc-600 hover:text-black transition-colors py-2"
                  data-testid="toggle-auth-mode"
                >
                  {isRegister ? '¿Ya tienes cuenta? Inicia sesión' : '¿No tienes cuenta? Regístrate'}
                </button>
              </div>
            </form>
              </>
            )}
          </div>
        </div>

        {/* Mobile Footer Brand Reminder */}
        <div className="lg:hidden pb-safe">
          <div className="text-center py-4 border-t border-zinc-100">
            <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-400">
              © 2026 ATELIER — JPM · Todos los derechos reservados
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export { LoginPage };
